import type { ThreadInteraction } from "@/services/api/agentic-v2/types";
import { ToolCallView } from "./ToolCallView";

export interface InteractionViewProps {
  interaction: ThreadInteraction;
  /** Highlight one step (e.g. a tool_call deep link). */
  highlightStep?: number | null;
}

/**
 * Staff-only (ops) visualisation of an interaction: its status and every step
 * with the otherwise-hidden tool input/output, confidence and disposition.
 * Reused by the entity preview bubble and the interaction deep-link panel.
 */
export function InteractionView({ interaction, highlightStep }: InteractionViewProps) {
  const steps = interaction.steps ?? [];

  return (
    <div className="space-y-3 text-left" data-testid="interaction-view">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-100">Interaction</span>
        <span className="inline-flex items-center rounded-md bg-slate-800/80 px-2 py-0.5 text-[11px] text-slate-200">
          {interaction.status}
        </span>
        <span className="text-[11px] text-slate-500">
          {steps.length} step{steps.length === 1 ? "" : "s"}
        </span>
      </div>

      {steps.length === 0 ? (
        <p className="text-xs text-slate-500">This interaction has no recorded steps.</p>
      ) : (
        <ol className="space-y-2">
          {steps.map((step, index) => {
            const highlighted = highlightStep != null && highlightStep === index;
            return (
              <li
                key={index}
                className={
                  highlighted
                    ? "rounded-lg border border-indigo-400/60 bg-indigo-500/10 p-2"
                    : "rounded-lg border border-white/5 p-2"
                }
              >
                {step.type === "tool_call" ? (
                  <ToolCallView step={step} index={index} />
                ) : (
                  <div className="space-y-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      {step.type}
                    </p>
                    {step.summary ? (
                      <p className="text-xs leading-relaxed text-slate-300">
                        {step.summary}
                      </p>
                    ) : step.content ? (
                      <p className="text-xs leading-relaxed text-slate-300">
                        {step.content}
                      </p>
                    ) : null}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
